/**
 * White-label theming engine.
 *
 * Tenants supply a `TenantTheme` config (logo, colors, product name) and
 * this module writes CSS custom properties onto `:root` so the entire UI
 * reskins without any component changes.
 *
 * Phase A: the theme is a compile-time constant (Analytica DSS defaults).
 * Phase B: the tenant's config is fetched from the API on app boot and
 *          applied via `applyTheme()`.
 */

export interface TenantTheme {
  /** Product name shown in the navbar and page titles */
  productName: string;
  /** Optional logo URL (SVG or PNG). null = text-only brand. */
  logoUrl: string | null;
  /** Badge text next to the logo (e.g. "Beta", "Enterprise") */
  badge: string;

  // --- Color tokens ---
  /** Page background */
  bg: string;
  /** Card surface (supports rgba for glassmorphism) */
  surface: string;
  /** Border color */
  border: string;
  /** Primary text */
  text: string;
  /** Muted/secondary text */
  muted: string;
  /** Primary accent color */
  accentBlue: string;
  /** Secondary accent */
  accentPurple: string;
  /** Tertiary accent */
  accentPink: string;

  // --- Gradient stops ---
  /** Background gradient blobs (20% top-left) */
  gradientBlob1: string;
  /** Background gradient blobs (80% bottom-right) */
  gradientBlob2: string;

  // --- Footer ---
  /** Whether to show the Analytica DSS credit footer */
  showAnalyticaCredit: boolean;
  /** Custom footer text (replaces tagline if set) */
  footerTagline: string;
}

/** Analytica DSS default theme — the factory skin. */
export const DEFAULT_THEME: TenantTheme = {
  productName: "TalentAlign",
  logoUrl: null,
  badge: "",

  bg: "#0b1120",
  surface: "rgba(15, 23, 42, 0.55)",
  border: "rgba(148, 163, 184, 0.18)",
  text: "#e2e8f0",
  muted: "#94a3b8",
  accentBlue: "#60a5fa",
  accentPurple: "#a78bfa",
  accentPink: "#f0abfc",

  gradientBlob1: "rgba(59, 130, 246, 0.15)",
  gradientBlob2: "rgba(167, 139, 250, 0.15)",

  showAnalyticaCredit: true,
  footerTagline:
    "Evidence-first resume matching \u00B7 Seven-signal ML scoring \u00B7 Bias-aware by design",
};

/** Example: corporate blue/teal white-label for a buyer demo */
export const EXAMPLE_CORPORATE_THEME: TenantTheme = {
  productName: "HireIQ",
  logoUrl: null,
  badge: "Enterprise",

  bg: "#0a1628",
  surface: "rgba(10, 22, 40, 0.6)",
  border: "rgba(100, 180, 220, 0.15)",
  text: "#e0f0ff",
  muted: "#7eb3d4",
  accentBlue: "#38bdf8",
  accentPurple: "#22d3ee",
  accentPink: "#67e8f9",

  gradientBlob1: "rgba(56, 189, 248, 0.12)",
  gradientBlob2: "rgba(34, 211, 238, 0.10)",

  showAnalyticaCredit: false,
  footerTagline: "Powered by advanced ML matching technology",
};

/**
 * Apply a theme by writing CSS custom properties onto :root.
 * Call this once at app boot (main.tsx) after loading the tenant config.
 */
export function applyTheme(theme: TenantTheme): void {
  const root = document.documentElement;
  root.style.setProperty("--ta-bg", theme.bg);
  root.style.setProperty("--ta-surface", theme.surface);
  root.style.setProperty("--ta-border", theme.border);
  root.style.setProperty("--ta-text", theme.text);
  root.style.setProperty("--ta-muted", theme.muted);
  root.style.setProperty("--ta-accent-blue", theme.accentBlue);
  root.style.setProperty("--ta-accent-purple", theme.accentPurple);
  root.style.setProperty("--ta-accent-pink", theme.accentPink);

  // Update background gradients
  document.body.style.backgroundImage = [
    `radial-gradient(at 20% 20%, ${theme.gradientBlob1} 0px, transparent 50%)`,
    `radial-gradient(at 80% 80%, ${theme.gradientBlob2} 0px, transparent 50%)`,
    `linear-gradient(135deg, ${theme.bg} 0%, ${lighten(theme.bg, 0.08)} 50%, ${theme.bg} 100%)`,
  ].join(", ");
}

/** Simple hex lightener for gradient mid-stop. */
function lighten(hex: string, amount: number): string {
  const h = hex.replace("#", "");
  const r = Math.min(255, parseInt(h.substring(0, 2), 16) + Math.round(255 * amount));
  const g = Math.min(255, parseInt(h.substring(2, 4), 16) + Math.round(255 * amount));
  const b = Math.min(255, parseInt(h.substring(4, 6), 16) + Math.round(255 * amount));
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

/**
 * Read the current theme from localStorage (set by tenant admin or API).
 * Falls back to DEFAULT_THEME.
 */
export function loadTheme(): TenantTheme {
  try {
    const raw = localStorage.getItem("talent-align-theme");
    if (raw) return { ...DEFAULT_THEME, ...JSON.parse(raw) };
  } catch { /* ignore corrupt data */ }
  return DEFAULT_THEME;
}

/** Persist a theme override to localStorage. */
export function saveTheme(theme: TenantTheme): void {
  localStorage.setItem("talent-align-theme", JSON.stringify(theme));
}
